import React, { Fragment, Suspense, useMemo, useState } from 'react';
import Container from '../components/Container';
import StickyHeader from '../components/StickyHeader';
import Header from '../components/Header';
import { useMenu } from '../hooks/useMenu';
import Loading from '../components/Loading';
import { Button, Divider, InputNumber, Modal, Radio, Tag } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { MenuItem } from '~/types/Menu';
import { CheckboxGroupProps } from 'antd/lib/checkbox';
import { MenuAvailabilityType } from '@restaurant/types';
import { api } from '@restaurant/api';
import message from '~/utils/message';
import {
  useMenuAvailability,
  useRefreshMenuAvailability,
} from '../hooks/useMenuAvailability';
import { MenuAvailability } from '@restaurant/api';

const options: CheckboxGroupProps['options'] = [
  { value: MenuAvailabilityType.ToggleOff, label: 'Stok Habis' },
  { value: MenuAvailabilityType.CountStock, label: 'Countdown' },
];

const DeleteForm = ({ id, onClose }: { id: string; onClose: () => void }) => {
  const [loading, setLoading] = useState(false);
  const refresh = useRefreshMenuAvailability();

  const onSubmit = async () => {
    setLoading(true);
    try {
      await api.deleteMenuAvailability(id);
      message.success('Berhasil set ketersediaan');
      onClose();
      refresh();
    } catch (e: any) {
      message.success('Gagal set ketersediaan: ' + e.message);
    }
    setLoading(false);
  };

  return (
    <div>
      <p>{`Set sebagai tersedia?`}</p>
      <div className="flex gap-2 justify-end mt-6">
        <Button block onClick={onClose}>
          Cancel
        </Button>
        <Button loading={loading} block type="primary" onClick={onSubmit}>
          Submit
        </Button>
      </div>
    </div>
  );
};

const ChangeForm = ({
  item,
  onClose,
}: {
  item: MenuItem;
  onClose: () => void;
}) => {
  const [type, setType] = useState(MenuAvailabilityType.ToggleOff);
  const [n, setN] = useState(5);
  const [loading, setLoading] = useState(false);
  const refresh = useRefreshMenuAvailability();
  const { id, name } = item;

  const needStock = type === MenuAvailabilityType.CountStock;

  const onSubmit = async () => {
    setLoading(true);
    try {
      await api.createMenuAvailability({
        menuId: id,
        availabilityType: type,
        ...(needStock
          ? {
              stockCounter: {
                start: n,
              },
            }
          : {}),
      });
      message.success('Berhasil set ketersediaan');
      onClose();
      refresh();
    } catch (e: any) {
      message.success('Gagal set ketersediaan: ' + e.message);
    }
    setLoading(false);
  };

  return (
    <div>
      <Radio.Group
        className="w-full mb-4"
        options={options}
        value={type}
        optionType="button"
        buttonStyle="solid"
        onChange={(e) => setType(e.target.value)}
      />
      {needStock && (
        <div className="mb-4">
          <span className="mr-2">Sisa</span>
          <InputNumber value={n} onChange={(val) => setN(val ?? 0)} min={1} />
        </div>
      )}
      <div className="flex gap-2 justify-end mt-6">
        <Button block onClick={onClose}>
          Cancel
        </Button>
        <Button loading={loading} block type="primary" onClick={onSubmit}>
          Submit
        </Button>
      </div>
    </div>
  );
};

const Row = ({
  item,
  availability,
}: {
  item: MenuItem;
  availability: MenuAvailability | undefined;
}) => {
  const { name } = item;
  const [show, setShow] = useState(false);

  const tag = (() => {
    if (!availability) {
      return (
        <Tag color="success" icon={<CheckCircleOutlined />}>
          Ada
        </Tag>
      );
    }

    const { availabilityType, stockCounter } = availability;

    if (
      availabilityType === MenuAvailabilityType.ToggleOff ||
      stockCounter?.current === 0
    ) {
      return (
        <Tag color="error" icon={<CloseCircleOutlined />}>
          Habis
        </Tag>
      );
    } else {
      return (
        <Tag color="warning" icon={<ExclamationCircleOutlined />}>
          {`Sisa (${availability.stockCounter?.current ?? 0})`}
        </Tag>
      );
    }
  })();

  return (
    <>
      <div className="flex items-center justify-between rounded border border-neutral-300 border-solid shadow-md px-4 py-2 mb-2">
        <p className="text-lg">{name}</p>
        <div onClick={() => setShow(true)}>
          {tag}
          <RightOutlined className="text-neutral-400" />
        </div>
      </div>
      <Modal
        open={show}
        title={name}
        onCancel={() => setShow(false)}
        footer={null}
        destroyOnClose
      >
        {availability ? (
          <DeleteForm
            id={availability.menuAvailabilityId}
            onClose={() => setShow(false)}
          />
        ) : (
          <ChangeForm item={item} onClose={() => setShow(false)} />
        )}
      </Modal>
    </>
  );
};

const MenuAvailabilityContent = () => {
  const menu = useMenu();
  const availability = useMenuAvailability();

  const availaibilityMap = useMemo(() => {
    return (
      availability?.reduce<Record<string, MenuAvailability | undefined>>(
        (acc, cur) => {
          acc[cur.menuId] = cur;
          return acc;
        },
        {}
      ) ?? {}
    );
  }, [availability]);

  return (
    <div className="p-4 flex flex-col">
      {menu
        .filter(({ items }) => items.length)
        .map(({ id: catId, name, items }) => (
          <Fragment key={catId}>
            <div key={catId}>
              <p className="text-xl mb-2">{name}</p>
              <div>
                {items.map((item) => (
                  <Row
                    key={item.id}
                    item={item}
                    availability={availaibilityMap[item.id]}
                  />
                ))}
              </div>
            </div>
            <Divider className="my-4" />
          </Fragment>
        ))}
    </div>
  );
};

const MenuAvailabilityPage = () => {
  return (
    <Container>
      <StickyHeader>
        <Header title="Menu Availability" />
        <Suspense fallback={<Loading />}>
          <MenuAvailabilityContent />
        </Suspense>
      </StickyHeader>
    </Container>
  );
};

export default MenuAvailabilityPage;
